import * as THREE from 'three'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { HatchEntity } from '@/plugins/cad/module/CADModule/model/entity/HatchEntity'
import { FillType } from '@/plugins/cad/module/CADModule/model/entity/HatchEntity'
import type { MaterialManager } from '@/plugins/cad/module/CADModule/object/material/MaterialManager'
import { BaseObjectManager, HighlightMode } from './BaseObjectManager'

type HatchObject = THREE.Mesh

/** 将 loops/holes 中的实体点转换为 THREE.Shape */
function buildShapeFromLoops(entity: HatchEntity): THREE.Shape | null {
  const loopKeys = Object.keys(entity.loops)
  if (loopKeys.length === 0) {
    return null
  }

  // 取第一个外轮廓
  const outerEntities = entity.loops[loopKeys[0]] as any[]
  const outerPoints: THREE.Vector2[] = extractPoints(outerEntities)
  if (outerPoints.length < 2) {
    return null
  }

  const shape = new THREE.Shape(outerPoints)

  // 孔洞
  const holeKeys = Object.keys(entity.holes)
  for (const key of holeKeys) {
    const holeEntities = entity.holes[key] as any[]
    const holePts = extractPoints(holeEntities)
    if (holePts.length >= 2) {
      const path = new THREE.Path(holePts)
      shape.holes.push(path)
    }
  }

  return shape
}

function extractPoints(entities: any[]): THREE.Vector2[] {
  const pts: THREE.Vector2[] = []
  for (const ent of entities) {
    if (ent.x !== undefined && ent.y !== undefined) {
      pts.push(new THREE.Vector2(ent.x, ent.y))
    } else if (ent.startPoint) {
      pts.push(new THREE.Vector2(ent.startPoint.x, ent.startPoint.y))
    }
  }
  return pts
}

/** 填充对象管理器 */
export class HatchObjectManager extends BaseObjectManager<HatchEntity, HatchObject> {
  constructor(parent: THREE.Object3D, materialManager: MaterialManager) {
    super(parent, materialManager, EntityType.Hatch)
  }

  protected createObject(entity: HatchEntity): HatchObject {
    const geometry = this.buildGeometry(entity)
    const material = this.buildMaterial(entity)
    const mesh = new THREE.Mesh(geometry, material)
    mesh.userData.entity = entity
    return mesh
  }

  private buildGeometry(entity: HatchEntity): THREE.BufferGeometry {
    const shape = buildShapeFromLoops(entity)
    if (!shape) {
      return new THREE.BufferGeometry()
    }
    return new THREE.ShapeGeometry(shape)
  }

  private buildMaterial(entity: HatchEntity): THREE.Material {
    const cached = this.getCachedMaterial(entity)
    if (cached) {
      return cached as THREE.Material
    }
    return this.createMaterialForEntity(entity)
  }

  private createMaterialForEntity(entity: HatchEntity): THREE.MeshBasicMaterial {
    if (entity.fillType === FillType.Gradient) {
      return new THREE.MeshBasicMaterial({
        color: entity.gcolor1 ?? 0xffffff,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
      })
    }

    return new THREE.MeshBasicMaterial({
      color: entity.color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8,
    })
  }

  protected onUpdateGeometry(entity: HatchEntity): void {
    const mesh = this.objectMap.get(entity.id)
    if (!mesh) {
      return
    }
    mesh.geometry.dispose()
    mesh.geometry = this.buildGeometry(entity)
  }

  protected onUpdateMaterial(entity: HatchEntity): void {
    const mesh = this.objectMap.get(entity.id)
    if (!mesh) {
      return
    }
    ;(mesh.material as THREE.Material).dispose()
    mesh.material = this.createMaterialForEntity(entity)
  }

  protected handleEntityHighlight(entity: HatchEntity, mode: HighlightMode): void {
    const mesh = this.objectMap.get(entity.id)
    if (!mesh) {
      return
    }
    switch (mode) {
      case HighlightMode.Hover:
        ;(mesh.material as THREE.MeshBasicMaterial).color.setHex(this.hoverMaterialOption.color)
        break
      case HighlightMode.Select:
        ;(mesh.material as THREE.MeshBasicMaterial).color.setHex(this.selectMaterialOption.color)
        break
      default:
        ;(mesh.material as THREE.MeshBasicMaterial).color.setHex(entity.color)
        break
    }
  }

  protected disposeObject(object: HatchObject): void {
    object.geometry.dispose()
    this.disposeMaterialFromObject(object)
  }
}
